import PropTypes from 'prop-types'
import { Button, Popconfirm } from 'antd'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { createUseStyles } from 'react-jss'
import { buildUrl } from '../../api/api-client'
import CircleCheckIcon from '../Common/CircleCheckIcon'

// Width of antd's expand-icon column. The expanded details are padded by the
// same amount so their columns line up with the primary card above them.
export const EXPAND_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  // Practice names get very long ("... Advanced Heart & Vascular Institute
  // (Dr. ...)"), so the title wraps onto as many lines as it needs instead of
  // widening the table or being truncated.
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  // Edit + delete share the last line, left-aligned with the text above.
  actionsLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  // Same grid, indented to align with the primary card past the expand column.
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
    paddingLeft: EXPAND_COLUMN_WIDTH,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#8c8c8c',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
  // Lets a field take the full width of the 2-column details grid.
  spanTwo: {
    gridColumn: '1 / -1',
  },
  logo: {
    display: 'block',
    width: 80,
    maxWidth: '100%',
    height: 'auto',
    marginTop: 2,
  },
})

function Field({ label, children = null, span = false }) {
  const classes = useStyles()
  return (
    <div
      className={span ? `${classes.field} ${classes.spanTwo}` : classes.field}
    >
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{children}</span>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  span: PropTypes.bool,
}

// Always-visible summary shown for every practice row on mobile.
export function PracticeMobileCard({ practice, onEdit, onDelete }) {
  const classes = useStyles()
  const { active, id, name, phoneNumber } = practice
  // Status is always present, so this line never renders empty; the phone is
  // dropped when missing so there is no dangling separator.
  const statusLine = [active ? 'Active' : 'Inactive', phoneNumber]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={classes.card}>
      <div className={classes.name}>{name || '—'}</div>
      <div className={classes.line}>
        <CircleCheckIcon checked={active} /> {statusLine}
      </div>
      <div className={classes.actionsLine}>
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => onEdit(id)}
          style={{ paddingLeft: 0 }}
        >
          Edit
        </Button>
        <Popconfirm
          title="Are you sure you want to delete this practice?"
          onConfirm={() => onDelete(id)}
          okText="Yes"
          cancelText="No"
          placement="topRight"
        >
          <Button
            type="link"
            size="small"
            icon={<DeleteOutlined />}
            className="delete-button"
          >
            Delete
          </Button>
        </Popconfirm>
      </div>
    </div>
  )
}

PracticeMobileCard.propTypes = {
  practice: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    active: PropTypes.bool,
    phoneNumber: PropTypes.string,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
}

// Fields hidden behind the per-row expand toggle: the long address and the
// rarely scanned fax number / logo.
export function PracticeMobileDetails({ practice }) {
  const classes = useStyles()
  const { address, faxNumber, logoPath, name } = practice

  return (
    <div className={classes.detailsGrid}>
      <Field label="Address" span>
        {address || '—'}
      </Field>
      <Field label="Fax number">{faxNumber || '—'}</Field>
      <Field label="Logo">
        {logoPath ? (
          <img
            className={classes.logo}
            src={buildUrl(logoPath)}
            alt={`logo-${name}`}
          />
        ) : (
          '—'
        )}
      </Field>
    </div>
  )
}

PracticeMobileDetails.propTypes = {
  practice: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string,
    faxNumber: PropTypes.string,
    logoPath: PropTypes.string,
  }).isRequired,
}
